import { CAMERA_TYPE } from "@/shared/state/visualization/schema/v1/three-d";

import type { BabylonState } from "../../state";

/**
 * World units of `radius` change per `WheelEvent.deltaY` pixel.
 *
 * Hand-tuned against a `~[2, 1000]` envelope (a unit-bounding-radius
 * scene); produces about 1 envelope-unit of zoom per 100 wheel pixels
 * at the close end and proportionally faster traversal at far ranges
 * via the camera's `wheelDeltaPercentage` interaction. Lower values
 * produce a heavier feel; higher values produce a more sensitive feel.
 */
export const ZOOM_SENS = 0.01;

/**
 * World units of `focusPoint.y` change per `WheelEvent.deltaY` pixel
 * for shift+wheel elevation. A typical wheel notch is `deltaY ≈ 100`,
 * so one notch moves the focus point by `100 * ELEVATE_SENS` world
 * units. Twice the zoom sensitivity, so a notch produces a meaningful
 * altitude swing without leap-frogging past nearby content in
 * small-scene scenarios.
 */
export const ELEVATE_SENS = 0.02;

/**
 * Add a wheel delta into the input accumulator consumed by
 * `updateCamera`. `updateCamera`'s third-person branch adds it to
 * `babylonCamera.radius` and zeroes the accumulator each frame;
 * `constrainCamera` runs immediately after and clamps the resulting
 * radius against the scene-derived envelope before render, so no
 * input-side clamp is needed here.
 */
export function applyZoomDelta(state: BabylonState, deltaY: number): void {
  state.input.cameraDeltas.zoom += deltaY * ZOOM_SENS;
}

/**
 * Wheel-event predicate. Mirrors `shouldLatchOrbit` / `shouldLatchPan`
 * shape for symmetry, even though the wheel handler is single-event
 * (no latch). Gates both the unmodified-wheel zoom path and the
 * shift+wheel elevation path; the modifier disambiguates which action
 * fires inside `attachZoomInput`.
 */
export function shouldZoom(state: BabylonState): boolean {
  return state.sceneConfig.camera?.spec.type === CAMERA_TYPE.thirdPerson;
}

/**
 * Add a wheel delta into the elevation channel of the input
 * accumulator. `updateCamera`'s third-person branch adds the
 * accumulated value to `camera.focusPoint.y` and zeroes the field each
 * frame. No clamp on `y` — the user may take the focus below the grid
 * plane intentionally.
 *
 * Direction inverts `deltaY` so scroll-up elevates and scroll-down
 * lowers, matching the zoom convention (up = closer/higher). No-op when
 * the active camera is not third-person; orbit cameras don't read the
 * elevation channel.
 */
export function applyElevateDelta(state: BabylonState, deltaY: number): void {
  const camera = state.sceneConfig.camera;
  if (!camera || camera.spec.type !== CAMERA_TYPE.thirdPerson) {
    return;
  }
  state.input.cameraDeltas.elevateY -= deltaY * ELEVATE_SENS;
}

/**
 * Attach a wheel handler to the BabylonManager's canvas. Returns a
 * cleanup that detaches the listener.
 *
 * Routes by modifier: shift + wheel elevates the focus point,
 * unmodified wheel zooms the camera radius. `passive: false` lets us
 * call `preventDefault()` to suppress the browser's default page-
 * scroll behavior while the cursor is over the canvas — without this,
 * scrolling the wheel zooms the camera AND scrolls the page.
 *
 * No-op for orbit cameras. (Note: orbit-mode wheel zoom currently
 * also scrolls the page, because `attachControl(canvas, true)` in
 * `updateSceneConfig` passes `noPreventDefault=true` to Babylon's
 * default `ArcRotateCameraMouseWheelInput`. That is pre-existing
 * behavior outside this handler's scope.)
 */
export function attachZoomInput(state: BabylonState): () => void {
  const canvas = state.context.canvas;

  const onWheel = (e: WheelEvent) => {
    if (!shouldZoom(state)) {
      return;
    }
    e.preventDefault();
    if (e.shiftKey) {
      // Browsers (Chrome, Edge, Firefox) remap Shift+wheel on a
      // discrete mouse wheel to the horizontal axis: `deltaY` is `0`
      // and the scroll amount lands in `deltaX`. Trackpads keep
      // independent `deltaY/deltaX` and aren't remapped. The fallback
      // `e.deltaY || e.deltaX` handles both. A Shift+horizontal-swipe
      // gesture on a trackpad would also be treated as elevation —
      // acceptable: any axis movement under Shift maps to elevation.
      applyElevateDelta(state, e.deltaY || e.deltaX);
    } else {
      applyZoomDelta(state, e.deltaY);
    }
  };

  canvas.addEventListener("wheel", onWheel, { passive: false });

  return () => {
    canvas.removeEventListener("wheel", onWheel);
  };
}
